import { Box, Grid, Skeleton } from '@mui/material';
import agent from '@root/agent';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useCloudProvider } from '@root/components/v2/content/CloudProviderBar/CloudProviderContext';
import ContentBox from '@root/components/v2/content/ContentBox';
import PageContainer from '@root/components/v2/layout/PageContainer';
import PageContent from '@root/components/v2/layout/PageContent';
import { useSelectedEnvironments } from '@root/hooks/environments';
import useTimeFilter from '@root/hooks/SelectTimeFilter';
import { PieChart } from '@root/components/ui/PieChart';
import { RISK_COLORS } from '@root/utilities/colors';
import useRedirectWithParams from '@root/hoc/redirection';
import useEnhancedQuery from '@root/query/useEnhancedQuery';
import { useCallback, useMemo } from 'react';
import { formatTimeFilter } from '@root/hooks/features';
import RuntimeExploitFunnel from './RuntimeExploitFunnel';
import VulnerabilityFindingsTrend from './VulnerabilityFindingsTrend';
import UniqueCvesTrend from './UniqueCvesTrend';
import CveSummaryCard from './CveSummaryCard';
import TableCard from './TableCard';
import { packageColumns, publicImageColumns } from './tableCardColumns';
import { K8S_VULNERABILITIES_TABS, k8sVulnerabilitiesSubTabPath } from '@root/constants/moduleTabs';
import { funnelStageParams } from '../funnelStageFilters';

dayjs.extend(utc);
dayjs.extend(timezone);
const TIMEZONE = dayjs.tz.guess();
const UNSCANNED_IMAGES_COLORS = {
  'Vulnerable Images': '#FFAB91',
  'Unscanned Images': '#FF9FAA',
  'Clean Images': '#a0aec0',
};

// The trend and table cards share one row height.
const CARD_HEIGHT = '300px';
// The CVE summary, severity and unscanned-images cards share the shorter summary row.
const SUMMARY_CARD_HEIGHT = '250px';
// Summary card height minus the 40px header.
const SUMMARY_CARD_BODY_HEIGHT = '210px';

const PUBLIC_IMAGE_COLUMNS = publicImageColumns();
const PACKAGE_COLUMNS = packageColumns();

const BREADCRUMB_ROOT = [
  { title: 'Kubernetes' },
  { title: 'Vulnerabilities', path: K8S_VULNERABILITIES_TABS.path('dashboard') },
];

const Dashboard = () => {
  const { cloudProvider } = useCloudProvider();
  const { selectedEnvironments } = useSelectedEnvironments();
  const { timeFilter } = useTimeFilter();
  const redirectWithParams = useRedirectWithParams();

  const { data: severityData, isFetching: isFetchingSeverityData } = useEnhancedQuery(
    ['get_vulnerabilities_severity', cloudProvider, selectedEnvironments.map(item => item.id)],
    () =>
      agent.Kubernetes.get_vulnerabilities_severity({
        environments: selectedEnvironments.map(item => item.id),
        clusters: [],
      }),
  );

  const { data: trendData, isFetching: isFetchingTrendData } = useEnhancedQuery(
    ['get_vulnerabilities_trend', cloudProvider, selectedEnvironments.map(item => item.id), timeFilter, TIMEZONE],
    () =>
      agent.Kubernetes.get_vulnerabilities_trend({
        environments: selectedEnvironments.map(item => item.id),
        clusters: [],
        date_filter: formatTimeFilter(timeFilter),
        timezone: TIMEZONE,
      }),
  );

  const { data: uniqueCvesTrendData, isFetching: isFetchingUniqueCvesTrendData } = useEnhancedQuery(
    [
      'get_vulnerabilities_unique_cves_trend',
      cloudProvider,
      selectedEnvironments.map(item => item.id),
      timeFilter,
      TIMEZONE,
    ],
    () =>
      agent.Kubernetes.get_vulnerabilities_unique_cves_trend({
        environments: selectedEnvironments.map(item => item.id),
        clusters: [],
        date_filter: formatTimeFilter(timeFilter),
        timezone: TIMEZONE,
      }),
  );

  const { data: cveSummaryData, isFetching: isFetchingCveSummaryData } = useEnhancedQuery(
    ['get_vulnerabilities_cve_summary', cloudProvider, selectedEnvironments.map(item => item.id)],
    () =>
      agent.Kubernetes.get_vulnerabilities_cve_summary({
        environments: selectedEnvironments.map(item => item.id),
        clusters: [],
      }),
  );

  const { data: publicImagesFixImpactData, isFetching: isFetchingPublicImagesFixImpactData } = useEnhancedQuery(
    ['get_vulnerabilities_top_public_images_fix_impact', cloudProvider, selectedEnvironments.map(item => item.id)],
    () =>
      agent.Kubernetes.get_vulnerabilities_top_public_images_fix_impact({
        environments: selectedEnvironments.map(item => item.id),
        clusters: [],
      }),
  );

  const { data: packagesFixImpactData, isFetching: isFetchingPackagesFixImpactData } = useEnhancedQuery(
    ['get_vulnerabilities_top_packages_fix_impact', cloudProvider, selectedEnvironments.map(item => item.id)],
    () =>
      agent.Kubernetes.get_vulnerabilities_top_packages_fix_impact({
        environments: selectedEnvironments.map(item => item.id),
        clusters: [],
      }),
  );

  const { data: funnelData, isFetching: isFetchingFunnelData } = useEnhancedQuery(
    ['get_vulnerabilities_funnel', cloudProvider, selectedEnvironments.map(item => item.id)],
    () =>
      agent.Kubernetes.get_vulnerabilities_funnel({
        environments: selectedEnvironments.map(item => item.id),
        clusters: [],
      }),
  );

  const { data: imageSummaryData, isFetching: isFetchingImageSummaryData } = useEnhancedQuery(
    ['get_vulnerabilities_image_summary', selectedEnvironments.map(item => item.id)],
    () =>
      agent.Kubernetes.get_vulnerabilities_image_summary({
        environments: selectedEnvironments.map(item => item.id),
        clusters: [],
      }),
  );

  const _severityData = useMemo(() => {
    if (isFetchingSeverityData) return [];
    const breakupData = severityData?.data;
    return {
      data: {
        total_risk: breakupData?.total,
        risk_breakup: [
          { name: 'Critical', value: breakupData?.critical },
          { name: 'High', value: breakupData?.high },
          { name: 'Medium', value: breakupData?.medium },
          { name: 'Low', value: breakupData?.low },
          { name: 'Unknown', value: breakupData?.unknown },
        ],
      },
    };
  }, [isFetchingSeverityData, severityData?.data]);

  const _unscannedImageSeverityData = useMemo(() => {
    if (isFetchingImageSummaryData) return [];
    const breakupData = imageSummaryData?.data;
    return {
      data: {
        total_unscanned: breakupData?.total_images,
        images: [
          { name: 'Vulnerable Images', value: breakupData?.total_vuln_images ?? 0 },
          { name: 'Unscanned Images', value: breakupData?.total_unscanned ?? 0 },
          { name: 'Clean Images', value: breakupData?.unknown ?? 0 },
        ],
      },
    };
  }, [isFetchingImageSummaryData, imageSummaryData?.data]);

  // A funnel stage drills down into the Vulnerabilities > Findings sub tab, the list the funnel counts, which reads
  // the stage's filters from the link (see ../funnelStageFilters). A severity drills down into the CVEs sub tab,
  // which reads `severity`.
  const funnelInUseEnabled = funnelData?.data?.in_use_enabled;
  const handleFunnelClick = useCallback(
    ({ key, label }) => {
      redirectWithParams({
        url: k8sVulnerabilitiesSubTabPath('findings', funnelStageParams(key, funnelInUseEnabled)),
        breadcrumbs: [...BREADCRUMB_ROOT, { title: label }],
      });
    },
    [funnelInUseEnabled, redirectWithParams],
  );

  const handleLegendClick = useCallback(
    item => {
      redirectWithParams({
        url: k8sVulnerabilitiesSubTabPath('cves', { severity: item.name.toLowerCase() }),
        breadcrumbs: [...BREADCRUMB_ROOT, { title: item.name.toUpperCase() }],
      });
    },
    [redirectWithParams],
  );

  const handleImagesLegendClick = useCallback(
    item => {
      if (item.name.includes('Vulnerable')) {
        redirectWithParams({
          url: k8sVulnerabilitiesSubTabPath('images'),
          breadcrumbs: [...BREADCRUMB_ROOT, { title: item.name }],
        });
      }
    },
    [redirectWithParams],
  );

  return (
    <PageContainer>
      <PageContent>
        <RuntimeExploitFunnel
          data={funnelData?.data}
          isLoading={isFetchingFunnelData}
          onStageClick={handleFunnelClick}
        />

        <Grid container direction={'row'} spacing={2} mt={0.5}>
          <Grid item xs={12} md={12} lg={4} xl={6}>
            <VulnerabilityFindingsTrend
              data={trendData?.data}
              isLoading={isFetchingTrendData}
              timeFilter={timeFilter}
              height={CARD_HEIGHT}
            />
          </Grid>
          <Grid item xs={12} md={12} lg={4} xl={6}>
            <UniqueCvesTrend
              data={uniqueCvesTrendData?.data}
              isLoading={isFetchingUniqueCvesTrendData}
              timeFilter={timeFilter}
              height={CARD_HEIGHT}
            />
          </Grid>
          <Grid item xs={12} md={12} lg={4} xl={6}>
            <CveSummaryCard
              data={cveSummaryData?.data}
              isLoading={isFetchingCveSummaryData}
              height={SUMMARY_CARD_HEIGHT}
            />
          </Grid>
          <Grid item xs={12} md={12} lg={4} xl={3}>
            <ContentBox
              title={'Vulnerabilities By Severity'}
              titleSize="medium"
              padding="0px"
              height={SUMMARY_CARD_HEIGHT}
              titleContainerStyle={{
                fontSize: '16px !important',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                padding: '11.04px 15px',
                margin: '0px',
              }}
              containerClassName={'border-box-no-pad'}
              paddingMode="content">
              {isFetchingSeverityData && _severityData ? (
                <Box display="flex" alignItems="center" gap="20px" padding="15px" height={SUMMARY_CARD_BODY_HEIGHT}>
                  <Box display="flex" justifyContent="center">
                    <Skeleton variant="circular" width="120px" height="120px" />
                  </Box>
                  <Box flex="1" minWidth="0">
                    <Box display="flex" justifyContent="space-between" marginBottom="15px">
                      <Skeleton width="60px" height="16px" />
                      <Skeleton width="40px" height="16px" />
                    </Box>
                    <Box display="flex" flexDirection="column" gap="8px">
                      {Array.from(new Array(4)).map((_, index) => (
                        <Box key={index} display="flex" justifyContent="space-between" alignItems="center">
                          <Box display="flex" alignItems="center" gap="8px">
                            <Skeleton variant="circular" width="12px" height="12px" />
                            <Skeleton width="60px" height="14px" />
                          </Box>
                          <Skeleton width="30px" height="14px" />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              ) : _severityData?.data?.length === 0 ? (
                <Box height={SUMMARY_CARD_BODY_HEIGHT} display="flex" alignItems="center" justifyContent="center">
                  <div>No Data</div>
                </Box>
              ) : (
                <PieChart
                  data={_severityData?.data?.risk_breakup || []}
                  config={RISK_COLORS}
                  size={150}
                  innerRadius={60}
                  outerRadius={68}
                  middleText={'CVEs'}
                  middleValue={_severityData?.data?.total_risk}
                  showTooltip={true}
                  showLegend={true}
                  legendTitle={'Severity'}
                  legendSubtitle={'Total'}
                  layout={'horizontal'}
                  containerHeight={SUMMARY_CARD_BODY_HEIGHT}
                  containerPadding={'15px'}
                  className="w-full"
                  legendClickable={true}
                  onLegendClick={handleLegendClick}
                />
              )}
            </ContentBox>
          </Grid>
          <Grid item xs={12} md={12} lg={4} xl={3}>
            <ContentBox
              title={'Unscanned Images'}
              titleSize="medium"
              padding="0px"
              height={SUMMARY_CARD_HEIGHT}
              titleContainerStyle={{
                fontSize: '16px !important',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                padding: '11.04px 15px',
                margin: '0px',
              }}
              containerClassName={'border-box-no-pad'}
              paddingMode="content">
              {isFetchingImageSummaryData && _unscannedImageSeverityData ? (
                <Box display="flex" alignItems="center" gap="20px" padding="15px" height={SUMMARY_CARD_BODY_HEIGHT}>
                  <Box display="flex" justifyContent="center">
                    <Skeleton variant="circular" width="120px" height="120px" />
                  </Box>
                  <Box flex="1" minWidth="0">
                    <Box display="flex" justifyContent="space-between" marginBottom="15px">
                      <Skeleton width="60px" height="16px" />
                      <Skeleton width="40px" height="16px" />
                    </Box>
                    <Box display="flex" flexDirection="column" gap="8px">
                      {Array.from(new Array(4)).map((_, index) => (
                        <Box key={index} display="flex" justifyContent="space-between" alignItems="center">
                          <Box display="flex" alignItems="center" gap="8px">
                            <Skeleton variant="circular" width="12px" height="12px" />
                            <Skeleton width="60px" height="14px" />
                          </Box>
                          <Skeleton width="30px" height="14px" />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              ) : _unscannedImageSeverityData?.data?.length === 0 ? (
                <Box height={SUMMARY_CARD_BODY_HEIGHT} display="flex" alignItems="center" justifyContent="center">
                  <div>No Data</div>
                </Box>
              ) : (
                <PieChart
                  data={_unscannedImageSeverityData?.data?.images || []}
                  config={UNSCANNED_IMAGES_COLORS}
                  size={150}
                  innerRadius={60}
                  outerRadius={68}
                  middleText={'Images'}
                  middleValue={_unscannedImageSeverityData?.data?.total_unscanned}
                  showTooltip={true}
                  showLegend={true}
                  legendTitle={'Images'}
                  legendSubtitle={'Total'}
                  layout={'horizontal'}
                  containerHeight={SUMMARY_CARD_BODY_HEIGHT}
                  containerPadding={'15px'}
                  className="w-full"
                  legendClickable={true}
                  onLegendClick={handleImagesLegendClick}
                />
              )}
            </ContentBox>
          </Grid>
          <Grid item xs={12} lg={6}>
            <TableCard
              title="Top 5 Public images with highest fix impact"
              columns={PUBLIC_IMAGE_COLUMNS}
              rows={publicImagesFixImpactData?.data}
              isLoading={isFetchingPublicImagesFixImpactData}
              noDataMessage="No fixable public images found"
              height={CARD_HEIGHT}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <TableCard
              title="Top 5 application packages with highest fix impact"
              columns={PACKAGE_COLUMNS}
              rows={packagesFixImpactData?.data}
              isLoading={isFetchingPackagesFixImpactData}
              noDataMessage="No fixable packages found"
              height={CARD_HEIGHT}
            />
          </Grid>
        </Grid>
      </PageContent>
    </PageContainer>
  );
};

export default Dashboard;
